import React from "react";
import { getSessionDetails } from "../../utils/userSession";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faApple, faLinux, faWindows } from "@fortawesome/free-brands-svg-icons";
import {
    faCheckCircle,
    faClock,
    faCode,
    faDesktop,
    faGlobe,
    faMobileScreen,
    faTabletScreenButton,
} from "@fortawesome/free-solid-svg-icons";
import { Box } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { labelTranslations } from "./common";

const getOsIcon = (osName: string): IconDefinition => {
    if (osName.startsWith("Mac") || osName.toLowerCase() === "ios") return faApple;

    if (osName.startsWith("Windows")) return faWindows;

    if (osName.startsWith("Ubuntu")) return faLinux;

    return null;
};

const deviceTypeIcon = {
    mobile: faMobileScreen,
    pc: faDesktop,
    tablet: faTabletScreenButton,
};

const SessionImage = ({ sessionData }) => {
    const userAgent = sessionData["user_agent"];

    let icon = null;
    if (sessionData["type"] === "service") {
        icon = faCode;
    } else if (userAgent["device"] === "pc") {
        icon = getOsIcon(userAgent["os"]["name"]);
        if (!icon) {
            icon = faDesktop;
        }
    } else if (userAgent["device"] in deviceTypeIcon) {
        icon = deviceTypeIcon[userAgent["device"]];
    }

    return (
        <Box
            sx={{
                minWidth: 64,
                height: 64,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
                borderRadius: 1,
            }}
        >
            {icon && <FontAwesomeIcon icon={icon} size="2x" />}
        </Box>
    );
};

const SessionDetails = ({ sessionData }) => {
    const { subTitle, title, lastUse, location } = getSessionDetails(sessionData);

    let lastUseComp = (
        <Box component="span" display="flex" alignItems="center" gap={1}>
            <FontAwesomeIcon icon={faClock} />
            {lastUse}
        </Box>
    );

    if (sessionData["current"])
        lastUseComp = (
            <Box component="span" display="flex" alignItems="center" gap={1}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#0a0" }} />
                <FormattedMessage {...labelTranslations.active} />
            </Box>
        );

    return (
        <Box
            sx={{
                padding: "10px",
                flex: 1,
            }}
        >
            <Box sx={{ fontSize: "1.1em", fontWeight: "bold" }}>{title}</Box>
            <Box sx={{ opacity: 0.8, minHeight: "30px" }}>{subTitle}</Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <FontAwesomeIcon icon={faGlobe} />
                {location}
            </Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                {lastUseComp}
            </Box>
        </Box>
    );
};

type UserSessionCardProps = {
    session: any;
    onClick: (data: any) => void;
};

const UserSessionCard: React.FC<UserSessionCardProps> = ({ session, onClick }) => {
    const sessionData = session;

    return (
        <Box
            onClick={() => onClick(sessionData["id"])}
            sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                border: "1px solid lightgray",
                margin: "10px",
                borderRadius: "5px",
                cursor: "pointer",
                "&:hover": {
                    bgcolor: "#eee",
                },
                alignItems: { xs: "center", sm: "flex-start" },
                padding: "15px",
            }}
        >
            <SessionImage sessionData={sessionData} />
            <SessionDetails sessionData={sessionData} />
        </Box>
    );
};

export default UserSessionCard;
